import { Box, BoxProps, Flex, HStack, Heading, Image, Spinner, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as GenerateIcon } from 'assets/icons/design_ai.svg';
import backgroundRemoverImage from 'assets/images/background_remover.png';
import thumbnailImage from 'assets/images/bg_remover_thumbnail.webp';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import { BACKGROUND_REMOVED, CLICK_ON_PAID_FEATURE, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useE2Client } from 'modules/E2ClientProvider';
import { RootState, useAppDispatch, useAppSelector, useAppStore } from 'modules/Editor/store';
import { selectHasActiveWidgetBackgroundRemoved } from 'modules/Editor/store/infographSelector';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { UserTypes, useUser } from 'modules/User';
import { PermissionBadge } from 'modules/User';
import { ToastContent, useToast } from 'modules/common/components/Toast';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeStr } from 'utils/capitalizeStr';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';

import { CommonGatedUpgradeModal } from '../Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from '../Modals/CommonGatedUpgradeModal.types';

export const BackgroundRemover = ({
  onSuccessfulRemove,
  sourceClicked,
  style,
  imageStyle,
}: {
  sourceClicked: string;
  onSuccessfulRemove?: (newUrl: string) => void;
  style?: BoxProps;
  imageStyle?: BoxProps;
}) => {
  const { isFreeUser, userType } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { client } = useE2Client();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { trackChameleon } = useChameleon();

  const store = useAppStore();
  const activeWidgets = useAppSelector(selectActiveWidgets) || [];
  const isBackgroundRemoved = useAppSelector(selectHasActiveWidgetBackgroundRemoved);
  const widgetId = activeWidgets[0]?.id;
  const widgetData = (store.getState() as RootState).infograph.widgets[widgetId] as ImageWidgetData;
  // Store original image for undo
  const originalImageUrl = widgetData?.url;

  const { t } = useTranslation('editor_widget_toolbar', {
    useSuspense: false,
    keyPrefix: 'imageToolbar.backgroundRemover',
  });

  const { t: editorToolbarTranslation } = useTranslation('editor_toolbar');

  const onBackgroundUndo = useCallback(() => {
    dispatch(
      updateWidget({
        widgetId,
        widgetData: {
          url: originalImageUrl,
          filter: {
            backgroundRemoval: {
              isBackgroundRemoved: false,
              originalImageUrl: '',
            },
          },
        },
      }),
    );
  }, [widgetId, originalImageUrl, dispatch]);

  const handleRemoveBackground = async () => {
    try {
      if (isFreeUser) {
        const userPlanStr = userType === 'default' ? 'Free' : userType;

        Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
          Feature: 'Background Remover',
          plan: capitalizeStr(userPlanStr),
        });

        onOpen();
        return;
      }

      if (!widgetData?.url || !client) return;

      setIsLoading(true);
      const s3ImageUrl = await client?.removeBackgroundImage(widgetData.url);
      onSuccessfulRemove?.(s3ImageUrl);
      Mixpanel.track(BACKGROUND_REMOVED.eventName, {
        from: sourceClicked,
      });
      toast({
        status: 'success',
        title: t('successfulToast'),
        duration: 5000,
        render: ({ title }) => (
          <ToastContent
            title={title}
            buttonTitle={editorToolbarTranslation('undoBtn.text')}
            handleClick={onBackgroundUndo}
          />
        ),
      });
    } catch (error) {
      console.error('Error removing background:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onClickUpgrade = () => {
    const userPlanStr = userType === 'default' ? 'Free' : userType;

    Mixpanel.track(UPGRADE.eventName, {
      Feature: 'Background Remover',
      'User Plan': capitalizeStr(userPlanStr),
    });
    trackChameleon(UPGRADE.eventName);

    setAnalyticsUpgradeFeatureProp('Background Remover');
    window.open(UPGRADE_REDIRECT_LINK, '_self');
  };

  return (
    <>
      <Box position='relative' {...style}>
        {(isBackgroundRemoved || (!isFreeUser && !widgetData?.url)) && (
          <Tooltip
            hasArrow
            placement='right'
            bg='black'
            px='4'
            py='2'
            borderRadius='base'
            isDisabled={isBackgroundRemoved}
            label={
              <Text maxW='120px' textAlign='center' fontSize='xs'>
                {t('noneSelectedTooltip')}
              </Text>
            }
          >
            <Box
              position='absolute'
              top='0'
              left='4'
              right='4'
              w='auto'
              h='100%'
              background='rgba(255, 255, 255, 0.6)'
              zIndex='1'
            />
          </Tooltip>
        )}
        <Flex
          role='group'
          position='relative'
          mx='4'
          borderRadius='4px'
          borderLeft='4px solid transparent'
          background='linear-gradient(white, white) padding-box, linear-gradient(180deg, var(--vg-colors-violet-500) 44.27%, #4307F8 100%) border-box'
        >
          {isFreeUser && <PermissionBadge permission='premium' position='absolute' top={-2} right={4} />}
          <Box
            onClick={handleRemoveBackground}
            as='button'
            p='3'
            borderRadius='4px'
            flexDir='column'
            justifyContent='space-around'
            _hover={{ backgroundColor: 'var(--vg-colors-a11yMenu-hover)' }}
            boxShadow='sm'
          >
            <HStack gap='2'>
              {isLoading ? (
                <Box
                  flexShrink='0'
                  w='72px'
                  h='72px'
                  borderRadius='2.57px'
                  background='#F1EDFD'
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  {...imageStyle}
                >
                  <Spinner color='#7047EB' speed='0.65s' size='lg' />
                </Box>
              ) : (
                <Image
                  src={backgroundRemoverImage}
                  w='72px'
                  h='72px'
                  role='presentation'
                  flexShrink='0'
                  borderRadius='2.57px'
                  backgroundImage='linear-gradient(45deg, #0000001A 25%, transparent 25%), linear-gradient(-45deg, #0000001A 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #0000001A 75%), linear-gradient(-45deg, transparent 75%, #0000001A 75%)'
                  backgroundSize='20px 20px'
                  backgroundPosition='0 0, 0 10px, 10px -10px, -10px 0px'
                  {...imageStyle}
                />
              )}
              <Flex flexDirection='column' textAlign='left'>
                <Heading
                  fontSize={'sm'}
                  marginBottom='2'
                  fontWeight='semibold'
                  display='flex'
                  gap='2'
                  alignItems='center'
                >
                  <GenerateIcon color={'#4307F8'} width='24px' height='24px' />
                  {t('title')}
                </Heading>
                <Text fontSize={'xs'}>{t('description')}</Text>
              </Flex>
            </HStack>
          </Box>
        </Flex>
      </Box>
      <CommonGatedUpgradeModal
        isOpen={isOpen}
        onClose={onClose}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={UserTypes.Premium}
        modalImg={thumbnailImage}
        modalType={ModalUpgradeTypeEnum.BackgroundRemover}
      />
    </>
  );
};
